import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { FormattedMessage } from 'react-intl';
import { Box, Icon, Tooltip, Typography } from '@popmenu/common-ui';
import { Info } from '@popmenu/web-icons';
import { Skeleton } from '@material-ui/lab';
import { findElement } from '../../../utils/dom';
import { formatNumber } from '../../../utils/numbers';
import { makeStyles } from '../../../utils/withStyles';
import { useMenuItemWithReviewsData } from '../../hooks/data/useMenuItemWithReviewsData';
import { useWindowSizeContext } from '../../../shared/WindowSizeProvider';
import { DishReview } from '../DishReview';

type DishModalReviewsProps = {
  dishListSlug: string | null | undefined
  menuItemId: number
  placeSlug: string | null | undefined
  reviewsSummary: string | null | undefined
}

const useStyles = makeStyles(theme => ({
  reviewsContainer: {
    '@media (min-width: 960px)': {
      margin: theme.spacing(-3, -2, -4, -2),
      maxHeight: '40vh',
      overflowY: 'auto',
      padding: theme.spacing(3, 2, 6, 2),
    },
    paddingBottom: theme.spacing(2),
  },
  reviewsSummary: {
    marginBottom: theme.spacing(5),
  },
  reviewsSummaryIcon: {
    color: '#0076CB',
    display: 'inline-block',
    marginLeft: theme.spacing(0.5),
    transform: 'scale(1.2)',
  },
  reviewsSummaryTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  title: {
    borderBottom: `2px solid ${theme.palette.text.primary}`,
    fontSize: '0.95rem',
    fontWeight: 'bold',
    margin: theme.spacing(6, 0, 3, 0),
    paddingBottom: theme.spacing(1),
    textAlign: 'center',
    textTransform: 'uppercase',
  },
}));

export const DishModalReviews = ({ dishListSlug, menuItemId, placeSlug, reviewsSummary }: DishModalReviewsProps) => {
  const classes = useStyles();
  const { isMobile } = useWindowSizeContext();

  const limit = 10;
  const { menuItemData, fetchMore, loading } = useMenuItemWithReviewsData({
    dishListSlug,
    limit,
    menuItemId,
    offset: 0,
    placeSlug,
  });
  const count = menuItemData?.paginatedReviews?.count ?? 0;
  const hasMore = menuItemData?.paginatedReviews?.hasMore ?? true;
  const offset = menuItemData?.paginatedReviews?.offset ?? 0;
  const reviews = menuItemData?.paginatedReviews?.records ?? [];

  // Reviews container has max-height with inner scrolling on desktop
  // and swaps to scrolling within fullscreen modal on mobile (< 960px screen width)
  const getScrollParent = () => findElement<HTMLDivElement>('.pm-modal-body');

  return (
    <Box component="section">
      <Typography className={classes.title} component="h2">
        {count > 0 ? (
          <FormattedMessage
            defaultMessage="Reviews ({count})"
            values={{
              count: formatNumber(count),
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Reviews"
          />
        )}
      </Typography>
      <Box className={classes.reviewsContainer}>
        <InfiniteScroll
          getScrollParent={isMobile ? getScrollParent : undefined}
          hasMore={hasMore}
          initialLoad={false}
          loadMore={() => {
            void fetchMore({
              // @ts-expect-error Mixed GraphQL query types
              updateQuery: (previousResult, { fetchMoreResult }) => {
                const key = placeSlug ? 'rootPlace' : 'dishList';
                const mergedRecords = [
                  // @ts-expect-error Mixed GraphQL query types
                  ...previousResult[key].menuItem?.paginatedReviews?.records ?? [],
                  // @ts-expect-error Mixed GraphQL query types
                  ...fetchMoreResult[key].menuItem?.paginatedReviews?.records ?? [],
                ];
                const finalResult = fetchMoreResult;
                // @ts-expect-error Mixed GraphQL query types
                if (finalResult[key].menuItem) {
                  // @ts-expect-error Mixed GraphQL query types
                  finalResult[key].menuItem.paginatedReviews.records = mergedRecords;
                }
                return finalResult;
              },
              variables: {
                approved: true,
                dishListSlug: dishListSlug ?? undefined,
                menuItemId,
                pagination: {
                  limit,
                  offset: offset + limit,
                  sortBy: 'createdAt',
                  sortDir: 'desc',
                },
                rootPlaceSlug: placeSlug ?? undefined,
              },
            });
          }}
          loader={(
            <React.Fragment>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </React.Fragment>
          )}
          useWindow={false}
        >
          <React.Fragment>
            {!loading && reviews.length === 0 && (
              <Typography>
                <FormattedMessage
                  defaultMessage="No reviews are available for this item yet."
                />
              </Typography>
            )}
            {!loading && reviews.length > 0 && (
              <React.Fragment>
                {reviewsSummary && (
                  <Box className={classes.reviewsSummary} component="aside">
                    <Typography className={classes.reviewsSummaryTitle} component="h3">
                      <FormattedMessage
                        defaultMessage="Reviewers say..."
                      />
                    </Typography>
                    <Typography>
                      {reviewsSummary}
                      <Tooltip
                        title={(
                          <FormattedMessage
                            defaultMessage="This review summary was generated with the help of AI, using information from the restaurant and reviewers. Generative AI is experimental and info quality may vary. Please read full user reviews on the restaurant website for the most accurate details and information."
                          />
                        )}
                      >
                        <Box className={classes.reviewsSummaryIcon} tabIndex={0}>
                          <Icon icon={Info} />
                        </Box>
                      </Tooltip>
                    </Typography>
                  </Box>
                )}
                {reviews.map(review => (
                  <DishReview
                    key={review.id}
                    review={review}
                  />
                ))}
              </React.Fragment>
            )}
          </React.Fragment>
        </InfiniteScroll>
      </Box>
    </Box>
  );
};
